import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ExpandedStockItem {
  ticker: string;
  isChartExpanded: boolean;
  order: number;
}

interface ExpandedStocksContextType {
  expandedStocks: Map<string, ExpandedStockItem>;
  toggleStockExpansion: (ticker: string, expanded: boolean, chartExpanded: boolean) => void;
  getStockPosition: (ticker: string) => { row: number; column: number } | null;
  resetExpanded: () => void;
}

const ExpandedStocksContext = createContext<ExpandedStocksContextType | undefined>(undefined);

export const ExpandedStocksProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [expandedStocks, setExpandedStocks] = useState<Map<string, ExpandedStockItem>>(new Map());

  const toggleStockExpansion = (ticker: string, expanded: boolean, chartExpanded: boolean) => {
    setExpandedStocks(prev => {
      const newMap = new Map(prev);
      
      if (!expanded) {
        // Remove if collapsed
        newMap.delete(ticker);
      } else {
        // Add or update if expanded
        const currentOrder = newMap.size;
        newMap.set(ticker, { ticker, isChartExpanded: chartExpanded, order: currentOrder });
      }
      
      return newMap;
    });
  };

  const getStockPosition = (ticker: string) => {
    if (!expandedStocks.has(ticker)) return null;
    
    const expandedCount = expandedStocks.size;
    const stockItem = expandedStocks.get(ticker);
    if (!stockItem) return null;
    
    // Maximum 2 stocks per row
    if (expandedCount <= 2) {
      // First row: columns 0, 1
      return { row: 0, column: stockItem.order % 2 };
    } else {
      // Row calculation based on order
      const row = Math.floor(stockItem.order / 2);
      const column = stockItem.order % 2;
      return { row, column };
    }
  };

  const resetExpanded = () => {
    setExpandedStocks(new Map());
  };

  return (
    <ExpandedStocksContext.Provider
      value={{ expandedStocks, toggleStockExpansion, getStockPosition, resetExpanded }}
    >
      {children}
    </ExpandedStocksContext.Provider>
  );
};

export const useExpandedStocks = () => {
  const context = useContext(ExpandedStocksContext);
  if (context === undefined) {
    throw new Error('useExpandedStocks must be used within a ExpandedStocksProvider');
  }
  return context;
};